import { useMemo } from "react";
import ChartOptionsContext from "components/charting/contexts/ChartOptionsContext";
import useCombinedAthletesAndGroups from "hooks/useCombinedAthletesAndGroups";
import { getInitialOptions } from "features/Charting/Charting.config";
import useMetadataStore from "providers/MetadataProvider/useMetadataStore";
import useMetadataCircuits from "providers/MetadataProvider/useMetadataCircuits";
import { Metadata, MetadataType } from "types";

interface ChartOptionsContextProps {
  children: React.ReactNode;
}

const getGroupLabel = (item: Metadata) =>
  item.type === MetadataType.EXERCISE ? item.metadata?.category : item.typeLabel;

function ChartOptionsProvider(props: ChartOptionsContextProps) {
  const metadata = useMetadataStore();
  const circuitMetadata = useMetadataCircuits();
  const athletesAndGroups = useCombinedAthletesAndGroups();

  // Filter out Score metadata from charting options (e.g., Hyrox Conditioning Score), and
  // merge in circuits. `useMetadataCircuits` already excludes no-input (round-count-only)
  // circuits, which are not plottable. The combined list is sorted by group label (the same
  // label the measure Autocomplete groups by) so "Circuit" interleaves alphabetically with
  // exercise categories instead of always trailing at the end.
  const filteredMetadata = useMemo(
    () =>
      [...metadata.filter((m) => m.type !== MetadataType.SCORE), ...circuitMetadata].sort((a, b) =>
        (getGroupLabel(a) ?? "").localeCompare(getGroupLabel(b) ?? "")
      ),
    [metadata, circuitMetadata]
  );

  const chartOptions = useMemo(
    () =>
      getInitialOptions({
        sources: athletesAndGroups,
        measures: filteredMetadata,
      }),
    [athletesAndGroups, filteredMetadata]
  );

  return <ChartOptionsContext.Provider value={chartOptions}>{props.children}</ChartOptionsContext.Provider>;
}

export default ChartOptionsProvider;
